import type { NewCoreIconProps } from '@atlaskit/icon';
import {
	createEditorToolbarActions,
	type ManifestEditorToolbarActions,
} from '@atlaskit/native-embeds-common';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
} from '@atlassian/native-embeds-core/types';

import {
	dbEntryMoreItems,
	dbSharedManifestFields,
	dbSharedUiConfig,
	dbToolbarItems,
} from './shared';

export const DB_ENTRY_EXPERIENCE_MANIFEST_ID: ExperienceManifestId =
	experienceManifestId('database-entry');

const isDatabaseEntryUrl = (url: string): boolean => {
	try {
		return (
			url.match(/\/wiki\/spaces\/~?[\d\w]+\/database\/\d+(\?.*)?$/) !== null &&
			new URL(url).searchParams.has('entryId')
		);
	} catch {
		return false;
	}
};

/**
 * Experience manifest for a DB entry embed (URL contains an entryId param).
 * Entry embeds do not support the "Always show title" toolbar option.
 *
 * Must be registered before DBExperienceManifest so that the more
 * specific URL match is evaluated first.
 */
export const DBEntryExperienceManifest: {
	readonly handleChildCommands: (
		_experience: Experience,
		_command: NativeEmbedChildCommand,
	) => void;
	readonly id: ExperienceManifestId;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	readonly lockResizeAspectRatio: false;
	readonly parameterDefaults: {};
	readonly shouldAutoConvertToNativeEmbed: true;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (url: string, _isFullScreen?: boolean) => string;
	readonly uiConfig: {
		readonly appearance: 'minimal';
		readonly editorToolbarActions: ManifestEditorToolbarActions<any>;
		readonly editorToolbarHandlers: {};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly showBorder: false;
		readonly showHeader: false;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
	};
} = {
	...dbSharedManifestFields,
	id: DB_ENTRY_EXPERIENCE_MANIFEST_ID,
	isUrlMatch: isDatabaseEntryUrl,
	parameterDefaults: {},
	uiConfig: {
		...dbSharedUiConfig,
		editorToolbarActions: createEditorToolbarActions({
			items: [...dbToolbarItems],
			moreItems: dbEntryMoreItems,
		}),
	},
} as const satisfies ExperienceManifest;
